<template>
  <Form
    ref="formRef"
    :validation-schema="validationSchema"
    class="form-container"
    @submit="onSubmit"
    v-slot="{ errors }"
    :initial-values="initialValuesProp || undefined"
  >
    <div
      v-for="field in fields"
      :key="field.name"
      :class="['form-input', field.width]"
    >
      <label :for="field.name">{{ field.label }}</label>

      <Field
        v-if="field.type === 'school-select'"
        :id="field.name"
        :name="field.name"
        v-model="formData[field.name]"
      >
        <SchoolSearchSelect
          ref="schoolSearchSelectRef"
          :productTypeId="productTypeId"
          :programTypeId="programTypeId"
          :lenderId="lenderId"
          :initialSchool="formData[field.name]"
          v-bind:disabled="isFieldDisabled(field)"
          @update:selectedSchool="handleSchoolSelect"
        />
      </Field>
      <Field
        v-else-if="field.type === 'select'"
        as="select"
        :id="field.name"
        :name="field.name"
        v-model="formData[field.name]"
        :disabled="isFieldDisabled(field)"
        @update:model-value="handleFieldChange(field, $event)"
      >
        <option
          v-for="option in field.options"
          :key="option.id"
          :value="option.id"
        >
          {{ option.name }}
        </option>
      </Field>
      <Field
        v-else-if="field.type === 'money'"
        :name="field.name"
        :disabled="isFieldDisabled(field)"
        :mode="mode"
        v-model="formData[field.name]"
        @change="handleFieldChange(field, $event)"
      >
        <CurrencyInput
          ref="currencyInputRefs"
          :name="field.name"
          :disabled="isFieldDisabled(field)"
          :mode="mode"
          :initialValue="formData[field.name]"
          v-model="formData[field.name]"
          @change="handleFieldChange(field, $event)"
        />
      </Field>
      <Field
        v-else-if="field.maskType"
        :id="field.name"
        :name="field.name"
        :type="field.type"
        v-model="formData[field.name]"
        :disabled="isFieldDisabled(field)"
        @update:model-value="handleFieldChange(field, $event)"
      />
      <Field
        v-else
        :id="field.name"
        :name="field.name"
        :type="field.type"
        v-model="formData[field.name]"
        :disabled="isFieldDisabled(field)"
        @update:model-value="handleFieldChange(field, $event)"
      />
      <ErrorMessage v-show="mode !== 'view'" :name="field.name" class="error" />
    </div>
    <div class="col-12" v-if="loanDetails.isParentLOC">
      <ParentAttestationConsent
        ref="parentAttestationConsentRef"
        v-model="formData.parentConsent"
        :disabled="isSubmitting || mode === FormModes.view || mode === FormModes.edit"
        :entityId="draw?.id"
        :entityTypeId="ConsentServiceEntityTypeIds.Draw"
        :details="draw?.parentAttestation || { consentGiven: false }"
        :mode="mode"
        @update:modelValue="formData.parentConsent = $event"
      />
    </div>
    <div class="col-12" v-if="showOverdrawnWarning">
      <span class="error">WARNING: You may overdraw this LOC limit. </span>
      <span
        >Please review all recent disbursements before placing a draw for this
        line of credit.</span
      >
    </div>
    <button
      v-if="mode !== 'view'"
      class="submit-button col-12"
      type="submit"
      :disabled="
        !isFormValid(errors) ||
        isSubmitting ||
        (loanDetails.isParentLOC && formData.parentConsent !== true && mode === FormModes.add)
      "
    >
      Submit
    </button>
  </Form>
</template>

<script setup lang="ts">
import { reactive, watchEffect, computed, ref, onMounted } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { Schema } from "yup";
import { $api } from "@/services/api1";
import { MaskType } from "@/composables/maskingUtils";
import SchoolSearchSelect from "@/components/shared/SchoolSearchSelect.vue";
import  {
  type School,
  type ServiceType,
  DrawStatuses,
  DrawStatusesToAllowUpdateCertMethod,
  DrawStatusesToAllowUpdateLoanPeriodDates,
  type MinimumDrawAmountResponse,
  LoanDetails,
} from "@/models/loans";
import { useDrawData } from "@/composables/loan/useDrawData";
import useFormatter from "@/use/formatter";
import useFunctions from "@/use/functions1";
import CurrencyInput from "@/components/inputs/CurrencyInput.vue";
import ParentAttestationConsent from "@/components/v2/consents/ParentAttestationConsent.vue";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { FormModes } from "@/types/form";

interface FormField {
  name: string;
  label: string;
  type: string;
  value: any;
  width: string;
  options?: any;
  showOnAdd: boolean;
  showOnEdit: boolean;
  disabledOnEdit: boolean;
  maskType?: MaskType;
}

const props = defineProps<{
  onSubmit: (formData: Record<string, any>) => Promise<void>;
  isSubmitting: boolean;
  fields: Array<FormField>;
  validationSchema: Schema;
  mode: "add" | "edit" | "view";
  programTypeId: number;
  productTypeId: number;
  lenderId: number;
  initialValues?: Record<string, any> | null;
  loanId: number;
  minimumDrawAmount: MinimumDrawAmountResponse;
  loanDetails: LoanDetails;
  draw: any;
}>();

const showOverdrawnWarning = ref(false);

// Template Refs
const parentAttestationConsentRef = ref<InstanceType<
  typeof ParentAttestationConsent
> | null>(null);

const schoolSearchSelectRef = ref<InstanceType<
  typeof SchoolSearchSelect
> | null>(null);

const formRef = ref<InstanceType<typeof Form> | null>(null);

const currencyInputRefs = ref<Array<InstanceType<typeof CurrencyInput>> | null>(
  null
);

const initialValuesProp = computed(() => {
  return mapDrawObject(props.initialValues);
});

const { formatDateInputDisplayValue, formatMoney } = useFormatter();
const { convertMoneyStringToNumber } = useFunctions();

const { getEnrollmentTermsAsync } = useDrawData(
  props.lenderId,
  props.productTypeId,
  props.programTypeId
);

const formData = reactive<Record<string, any>>({});

const formErrors = computed<
  Partial<Record<string, string | undefined>> | undefined
>(() => formRef.value?.errors || undefined);

const isFormValid = computed(() => {
  return (errors: Partial<Record<string, string | undefined>>) =>
    errors && Object.keys(errors).length === 0;
});

const getCurrencyMask = (value) => {
  let cleanedValue =
    typeof value === "string" ? convertMoneyStringToNumber(value) : value;
  let formattedValue = !isNaN(cleanedValue)
    ? formatMoney(cleanedValue, 2)
    : null;

  return {
    value: value,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
};

const requestedAmount = computed(() => {
  const requestedAmount = formData?.requestedAmount;
  let cleanedValue =
    typeof requestedAmount === "string"
      ? convertMoneyStringToNumber(requestedAmount)
      : requestedAmount;
  let formattedValue = !isNaN(cleanedValue)
    ? formatMoney(cleanedValue, 2)
    : null;

  return {
    value: formData?.requestedAmount,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
});

const certifiedAmount = computed(() => {
  const certifiedAmount = formData?.certifiedAmount;
  let cleanedValue =
    typeof certifiedAmount === "string"
      ? convertMoneyStringToNumber(certifiedAmount)
      : certifiedAmount;
  let formattedValue = !isNaN(cleanedValue) ? formatMoney(cleanedValue) : null;

  return {
    value: formData?.requestedAmount,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
});

const loadOverdrawnWarning = async () => {
  if (props.mode !== "add") {
    showOverdrawnWarning.value = false;
    return;
  }
  showOverdrawnWarning.value = !(await $api.loans.canRequestFundAsync(
    props.loanId
  ));
};

function isFieldDisabled(field: FormField) {
  const disabledOnEdit = props.mode === "edit" && field.disabledOnEdit === true;

  return props.mode === "view" || props.isSubmitting || disabledOnEdit;
}

function getField(name: string): FormField | undefined {
  return props.fields.find((f) => f.name == name);
}

function mapDrawObject(selectedDraw: any) {
  if (!selectedDraw) return null;

  const schoolSelect = schoolSearchSelectRef.value?.[0];
  const school = schoolSelect
    ? (selectedDraw?.schoolDetailsId
        ? schoolSelect.findSchoolWithDetailId(selectedDraw.schoolDetailsId)
        : undefined)
      ?? schoolSelect.findSchool(selectedDraw?.schoolCode, selectedDraw?.schoolBranch)
    : undefined;
  const certificationMethod = getField("certificationMethodId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.certificationMethodId
  );
  const enrollmentStatus = getField("enrollmentStatus")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.enrollmentStatus
  );
  const enrollmentTerm = getField("enrollmentTermId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.enrollmentTermId
  );
  const drawStatus = getField("drawStatusTypeId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.drawStatusTypeId
  );
  const gradeLevel = getField("schoolLevelTypeId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.schoolLevelTypeId
  );

  const requestedAmountFormatted = selectedDraw.requestedAmount
    ? formatMoney(selectedDraw.requestedAmount)
    : null;
  const certifiedAmountFormatted =
    selectedDraw.certifiedAmount || selectedDraw.certifiedAmount === 0
      ? formatMoney(selectedDraw.certifiedAmount)
      : null;

  return {
    ...selectedDraw,
    requestedAmountFormatted: requestedAmountFormatted,
    certifiedAmountFormatted: certifiedAmountFormatted,
    schoolName: school?.schoolName,
    schoolLevelTypeName: selectedDraw?.schoolLevelTypeName || gradeLevel?.name,
    certificationMethodTypeName: certificationMethod?.name,
    enrollmentStatusName: enrollmentStatus?.name,
    enrollmentTermName: enrollmentTerm?.name,
    drawStatusTypeName: drawStatus?.name,
    anticipatedGraduationDateDisplay: formatDateInputDisplayValue(
      selectedDraw.anticipatedGraduationDate
    ),
    loanPeriodStartDateDisplay: formatDateInputDisplayValue(
      selectedDraw.loanPeriodStartDate
    ),
    loanPeriodEndDateDisplay: formatDateInputDisplayValue(
      selectedDraw.loanPeriodEndDate
    ),
    createdAtUtc: formatDateInputDisplayValue(selectedDraw.createdAtUtc),
    createdBy: selectedDraw.createdBy,
  };
}

function handleFieldChange(field: FormField, value: any) {
  switch (field.name) {
    case "schoolLevelTypeId":
      formData.schoolLevelTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "certificationMethodId":
      formData.certificationMethodTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "enrollmentStatus":
      formData.enrollmentStatusName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "enrollmentTermId":
      formData.enrollmentTermName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "drawStatusTypeId": {
      formData.drawStatusTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      const cmField = getField("certificationMethodId");
      if (cmField) {
        cmField.disabledOnEdit = !DrawStatusesToAllowUpdateCertMethod.includes(
          Number(value) as DrawStatuses
        );
      }

      const allowUpdateLoanPeriod =
        DrawStatusesToAllowUpdateLoanPeriodDates.includes(
          Number(value) as DrawStatuses
        );
      for (const key of ["loanPeriodStartDate", "loanPeriodEndDate"] as const) {
        const f = getField(key);
        if (f) f.disabledOnEdit = !allowUpdateLoanPeriod;
      }
      break;
    }
    case "certifiedAmount":
      formData.certifiedAmountFormatted =
        getCurrencyMask(value)?.formattedValue;
      break;
    case "requestedAmount":
      formData.requestedAmountFormatted = formatMoney(
        typeof value === "string" ? convertMoneyStringToNumber(value) : value
      );
      break;
    case "anticipatedGraduationDate":
      formData.anticipatedGraduationDateDisplay =
        formatDateInputDisplayValue(value);
      break;
    case "loanPeriodStartDate":
      formData.loanPeriodStartDateDisplay = formatDateInputDisplayValue(value);
      break;
    case "loanPeriodEndDate":
      formData.loanPeriodEndDateDisplay = formatDateInputDisplayValue(value);
      break;
    case "cluid":
    case "sequenceNumber":
      break;
    default:
      break;
  }

	formRef.value?.validateField(field.name, value);
}

function discardChanges() {
  resetSchoolField();
  formRef.value?.resetForm();
}

function resetSchoolField() {
  if (schoolSearchSelectRef.value && schoolSearchSelectRef.value[0]) {
    const school =
      schoolSearchSelectRef.value && schoolSearchSelectRef.value[0]
        ? schoolSearchSelectRef.value[0].findSchool(
            initialValuesProp.value?.schoolCode,
            initialValuesProp.value?.schoolBranch
          )
        : undefined;

    schoolSearchSelectRef.value[0].discardChanges(school);
  }
}

async function handleSchoolSelect(selectedSchool: School) {
  formData.school = selectedSchool;

  formData.schoolName = selectedSchool.schoolName;

  // set ids, and code/branch from school
  formData.schoolId = selectedSchool.id;
  formData.schoolDetailsId = selectedSchool.schoolDetailsId;
  formData.schoolCode = selectedSchool.schoolDOEId;
  formData.schoolBranch = selectedSchool.schoolBranchDOEId;

  if (props.mode == "add" && selectedSchool.id) {
    const enrollmentTerms = await getEnrollmentTermsAsync(
      true,
      selectedSchool.id,
      null,
      null
    );
    const field = props.fields.find((f) => f.name == "enrollmentTermId");

    if (field && enrollmentTerms) {
      field.options = enrollmentTerms;
    }
  }
}

async function onSubmit() {
  if (props.onSubmit) {
    await props.onSubmit({ ...formData });
  }
}

onMounted(loadOverdrawnWarning);

watchEffect(() => {
  props.fields.forEach((field) => {
    formData[field.name] = field.value;
    handleFieldChange(field, field.value);
  });
});

defineExpose({
  formErrors,
  requestedAmount,
  certifiedAmount,
  currencyInputRefs,
	formData,
	parentAttestationConsentRef,
  schoolSearchSelectRef,
  initialValuesProp,
  discardChanges,
  validate: () => formRef.value?.validate(),
});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
